import { useFileBrowser } from "../hooks/useFileBrowser";
import React, { useState } from "react";
import { useMsal } from "@azure/msal-react";
import {
  Body1,
  Button,
  Card,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  MessageBar,
  MessageBarBody,
  MessageBarActions,
  Option,
  Spinner,
  Subtitle2,
  Text,
  Title3,
  Tree,
  TreeItem,
  TreeItemLayout,
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
} from "@fluentui/react-components";
import {
  ArrowLeft24Regular,
  ArrowRight24Regular,
  Checkmark24Regular,
  Dismiss24Regular,
  Document24Regular,
  DocumentMultiple24Regular,
  FolderOpen24Regular,
  FolderRegular,
} from "@fluentui/react-icons";
import { Page } from "../components/App";
import { useJob } from "../context/JobContext";
import { listFilesByIds } from "../services/fileService"; // Updated import
import { getAccessToken } from "../services/authService"; // New import
import {
  RejectedSelection,
  splitSelectionByLikelyBackendPolicy,
} from "../services/fileSelectionValidation";
import { updateOpenFolderItems } from "./folderTreeState";
import { taskPaneBackground } from "../theme";

interface FileType {
  name: string;
  url: string;
  size_bytes?: number;
  driveId?: string;
  id?: string;
}

interface FileSelectionPageProps {
  onNavigate: (page: Page) => void;
}

/* ═══════════════════════════════════════════════════════════════════
 * STYLES
 *
 * Layout strategy:
 * ┌─ root (88vh, flex-col, overflow hidden) ────────────────────┐
 * │  headerCard   (flexShrink 0 — fixed at top)                 │
 * │  main         (flex 1, overflowY auto — scrollable body)    │
 * │    ├ folder panel OR folder summary row                     │
 * │    └ files panel (natural height, vh-capped list)           │
 * │  footerCard   (flexShrink 0 — fixed at bottom)              │
 * └─────────────────────────────────────────────────────────────┘
 *
 * Key design decisions for small-screen friendliness:
 * - Folder tree collapses to a one-line summary after selection,
 *   freeing ~200px for the file list on 13″ laptops.
 * - Both the folder tree and file list panels use flexShrink:0
 *   with fixed maxHeight caps.  They NEVER compress — the `main`
 *   scrollable area grows and shows a scrollbar instead.  This
 *   guarantees two visually distinct boxes with clear spacing
 *   between them regardless of viewport height.
 * ═══════════════════════════════════════════════════════════════ */
const useStyles = makeStyles({
  /* ── Outer shell ────────────────────────────────────────────── */
  root: {
    height: "88vh",
    display: "flex",
    flexDirection: "column",
    ...taskPaneBackground,
    ...shorthands.gap(tokens.spacingVerticalM), // Visible blue breathing room between header/main/footer.
    ...shorthands.padding(tokens.spacingVerticalL, tokens.spacingHorizontalL),
    boxSizing: "border-box",
    overflow: "hidden",
  },

  /* ── Header card (pinned top) ───────────────────────────────── */
  headerCard: {
    backgroundColor: tokens.colorNeutralBackground1,
    position: "relative",
    overflow: "hidden",
    ...shorthands.border("1px", "solid", "#dbe4ef"),
    ...shorthands.borderRadius("14px"),
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalXL),
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap("2px"),
    flexShrink: 0, // Header never compresses.
  },
  headerTitle: {
    margin: 0,
  },
  headerBody: {
    margin: 0,
    color: tokens.colorNeutralForeground3,
  },
  panelCard: {
    position: "relative",
    overflow: "hidden",
    ...shorthands.border("1px", "solid", "#dbe4ef"),
    ...shorthands.borderRadius("12px"),
    ...shorthands.padding("16px"),
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow4,
  },
  panelHeadingRow: {
    display: "flex",
    alignItems: "flex-start",
    ...shorthands.gap("12px"),
  },
  panelHeading: {
    margin: 0,
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
  },
  iconBox: {
    display: "flex",
    width: "36px",
    height: "36px",
    flexShrink: 0,
    alignItems: "center",
    justifyContent: "center",
    color: "#1d4ed8",
    ...shorthands.border("1px", "solid", "#dbeafe"),
    ...shorthands.borderRadius("8px"),
    backgroundColor: "#eff6ff",
  },
  smallIconBox: {
    display: "flex",
    width: "28px",
    height: "28px",
    alignItems: "center",
    justifyContent: "center",
    color: "#4338ca",
    ...shorthands.border("1px", "solid", "#e0e7ff"),
    ...shorthands.borderRadius("6px"),
    backgroundColor: "#eef2ff",
  },
  fileIcon: {
    flexShrink: 0,
    color: "#2563eb",
  },
  primaryButton: {
    color: "#1e3a8a",
    backgroundColor: "#dbeafe",
    ...shorthands.border("1px", "solid", "#bfdbfe"),
    boxShadow: "none",
    ":hover": {
      color: "#172554",
      backgroundColor: "#bfdbfe",
      ...shorthands.borderColor("#93c5fd"),
    },
  },
  secondaryButton: {
    color: tokens.colorNeutralForeground2,
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border("1px", "solid", "#dbeafe"),
    boxShadow: "none",
    ":hover": {
      color: "#1e3a8a",
      backgroundColor: "#eff6ff",
      ...shorthands.borderColor("#bfdbfe"),
    },
  },
  warningMessage: {
    ...shorthands.border("1px", "solid", "#fcd34d"),
    ...shorthands.borderRadius("10px"),
    backgroundColor: "#fffbeb",
  },
  errorMessage: {
    ...shorthands.border("1px", "solid", "#fecdd3"),
    ...shorthands.borderRadius("10px"),
    backgroundColor: "#fff1f2",
  },
  dialogSurface: {
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    ...shorthands.borderRadius("14px"),
    boxShadow: tokens.shadow64,
  },

  /* ── Main scrollable area ───────────────────────────────────── */
  main: {
    flex: 1,
    minHeight: 0, // Required so the flex child can shrink below its content height.
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap(tokens.spacingVerticalM),
    paddingRight: tokens.spacingHorizontalXXS,
    paddingBottom: tokens.spacingVerticalS, // Breathing room so last block scrolls clear of footer.
  },

  /* ── Folder browser — expanded state ────────────────────────── */
  folderPanel: {
    display: "flex",
    flexDirection: "column",
    flexShrink: 0, // Never compress — main area scrolls instead.
    ...shorthands.gap(tokens.spacingVerticalS),
  },
  folderHint: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200, // Compact hint text.
  },
  folderTreeFrame: {
    // Fixed cap — the tree never compresses; the outer main area scrolls instead.
    flexShrink: 0,
    minHeight: "100px", // Enough for ~3 folder rows so the tree stays usable.
    maxHeight: "200px", // Fixed cap (not vh) so both tree + file list fit predictably.
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border("1px", "solid", "#dbe4ef"),
    ...shorthands.borderRadius("12px"),
    ...shorthands.padding(tokens.spacingVerticalS),
    overflow: "hidden",
  },
  folderTreeScroll: {
    height: "100%",
    overflowY: "auto",
  },
  folderActions: {
    // Clear visual separation between the folder tree and the file list below.
    display: "flex",
    alignItems: "center",
    ...shorthands.gap(tokens.spacingHorizontalS),
    ...shorthands.padding(tokens.spacingVerticalXS, "0"),
  },

  /* ── Folder summary — collapsed state ───────────────────────── */
  // Shown after the user selects a folder and files are loaded.
  // Single-line row: 📁 FolderName ···················· [Change]
  folderSummaryRow: {
    display: "flex",
    alignItems: "center",
    ...shorthands.gap(tokens.spacingHorizontalS),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border("1px", "solid", "#dbe4ef"),
    ...shorthands.borderRadius("12px"),
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM),
    flexShrink: 0, // Summary is a single row — never needs to compress.
  },
  folderSummaryIcon: {
    flexShrink: 0,
    color: "#F7D154",
    fontSize: "20px",
    display: "flex",
    alignItems: "center",
  },
  folderSummaryName: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontWeight: tokens.fontWeightSemibold,
  },

  /* ── Files section ──────────────────────────────────────────── */
  filesPanel: {
    // Elastic height: consume remaining space below folder controls on tall panes.
    // On short panes it can shrink (with internal scroll in filesListFrame).
    flex: "1 0 180px",
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap(tokens.spacingVerticalS),
  },
  filesHeaderRow: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignItems: "center",
    ...shorthands.gap(tokens.spacingHorizontalS, tokens.spacingVerticalXS),
  },
  filesHeaderLeft: {
    // Groups "Files" title + selection count on the same baseline.
    display: "flex",
    alignItems: "center",
    ...shorthands.gap(tokens.spacingHorizontalS),
    flexShrink: 0,
  },
  filesTitle: {
    display: "inline", // Inline so the count label sits right beside it.
  },
  filesCount: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  filesHeaderButtons: {
    display: "flex",
    flexWrap: "wrap",
    ...shorthands.gap(tokens.spacingHorizontalXS),
  },
  subtleOutlinedButton: {
    ...shorthands.border("1px", "solid", tokens.colorBrandStroke1),
  },
  filesListFrame: {
    // Elastic file list container:
    // - fills remaining space on larger panes
    // - shrinks on smaller panes
    // - always scrolls internally when content exceeds available height
    flex: 1,
    minHeight: "120px",
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border("1px", "solid", "#dbe4ef"),
    ...shorthands.borderRadius("12px"),
    overflow: "hidden",
  },
  filesListScroll: {
    height: "100%",
    overflowY: "auto",
  },
  fileRow: {
    display: "flex",
    alignItems: "center",
    ...shorthands.gap(tokens.spacingHorizontalS),
    ...shorthands.padding(tokens.spacingVerticalXS, tokens.spacingHorizontalM),
    ...shorthands.borderBottom("1px", "solid", "#e7edf5"),
    transitionProperty: "background-color",
    transitionDuration: tokens.durationFaster,
    "&:hover": {
      backgroundColor: "#f6f9fd",
    },
  },

  /* ── Rejected files inline warning ──────────────────────────── */
  rejectedPreviewList: {
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.padding(tokens.spacingVerticalXS, tokens.spacingHorizontalS),
    maxHeight: "120px", // Reduced from 180px — keeps more room for the file list.
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap(tokens.spacingVerticalXXS),
  },
  rejectedPreviewTitle: {
    display: "block",
    color: tokens.colorPaletteDarkOrangeForeground2,
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
  },
  rejectedPreviewItem: {
    display: "block",
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    overflowWrap: "anywhere",
  },

  /* ── Footer card (pinned bottom) ────────────────────────────── */
  footerCard: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border("1px", "solid", "#dbe4ef"),
    ...shorthands.borderRadius("14px"),
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalXL),
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
    flexShrink: 0,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    ...shorthands.gap(tokens.spacingHorizontalS),
  },
  footerActions: {
    display: "flex",
    ...shorthands.gap(tokens.spacingHorizontalS),
    flexWrap: "nowrap", // Keep Back/Continue horizontally aligned; short labels on narrow panes.
    width: "100%",
    justifyContent: "space-between",
    alignItems: "center",
  },
  footerButton: {
    minHeight: "44px",
    whiteSpace: "nowrap",
    textAlign: "center",
    minWidth: 0,
  },
  footerLabelLong: {
    display: "inline",
    "@media (max-width: 520px)": {
      display: "none",
    },
  },
  footerLabelShort: {
    display: "none",
    "@media (max-width: 520px)": {
      display: "inline",
    },
  },

  /* ── Folder tree node styling ───────────────────────────────── */
  treeNode: {
    display: "inline-flex",
    alignItems: "center",
    ...shorthands.gap(tokens.spacingHorizontalXS),
    ...shorthands.borderRadius(tokens.borderRadiusSmall),
    ...shorthands.padding("2px", "4px"),
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  treeNodeSelected: {
    backgroundColor: "#D6E8FF",
  },
});

export const FileSelectionPage: React.FC<FileSelectionPageProps> = ({ onNavigate }) => {
  const styles = useStyles();
  const { instance } = useMsal();
  const { state, dispatch } = useJob();
  const [fetchedFiles, setFetchedFiles] = useState<FileType[]>([]);
  const [isFilesLoading, setIsFilesLoading] = useState(false);
  const [path, setPath] = useState<
    Array<{ driveId: string; itemId: string; name: string; webUrl?: string }>
  >([]);
  const [selectedFolder, setSelectedFolder] = useState<{
    driveId: string;
    itemId: string;
    name: string;
    webUrl?: string;
  } | null>(null);
  const [isEmptyFolderDialogOpen, setIsEmptyFolderDialogOpen] = useState(false);
  const [emptyFolderName, setEmptyFolderName] = useState<string | null>(null);
  const [isFileValidationDialogOpen, setIsFileValidationDialogOpen] = useState(false);
  const [pendingRejectedFiles, setPendingRejectedFiles] = useState<RejectedSelection[]>([]);
  const [invalidSelections, setInvalidSelections] = useState<RejectedSelection[]>([]);

  // Controls whether the folder tree is expanded or collapsed to a summary row.
  // Starts expanded; collapses automatically once files are fetched so the file list
  // gets maximum vertical space.  The user can re-expand via the "Change" button.
  const [isFolderBrowserExpanded, setIsFolderBrowserExpanded] = useState(true);

  const resetFileSelection = React.useCallback(() => {
    setIsFilesLoading(false);
    setSelectedFolder(null);
    setFetchedFiles([]);
    setPath([]);
    setInvalidSelections([]);
    setIsEmptyFolderDialogOpen(false);
    dispatch({ type: "SET_SELECTED_FILES", payload: [] });
    setIsFolderBrowserExpanded(true);
  }, [dispatch]);
  const {
    availableSources,
    selectedSource,
    setSelectedSourceId,
    appFolder,
    appFolderError,
    sourceWarning,
    isAppFolderLoading,
    isFoldersLoading,
    nodes,
    nodesRef,
    openItems,
    setOpenItems,
    error,
    setError,
    sourceRequests,
    loadChildren,
    reloadSources,
  } = useFileBrowser(resetFileSelection);

  const hasFolderSelection = path.length > 0;

  const upsertInvalidSelection = (entry: RejectedSelection) => {
    setInvalidSelections((prev) => {
      const filtered = prev.filter((item) => item.file.url !== entry.file.url);
      return [...filtered, entry];
    });
  };

  const removeInvalidSelectionByUrl = (url: string) => {
    setInvalidSelections((prev) => prev.filter((item) => item.file.url !== url));
  };

  const handleFileSelection = (file: FileType) => {
    if (!file.driveId || !file.id) {
      setError(
        "This file is missing its Microsoft Graph identifiers. Refresh the folder and try again."
      );
      return;
    }
    const isValidChecked = state.selectedFiles.some((f) => f.url === file.url);
    const isInvalidChecked = invalidSelections.some((item) => item.file.url === file.url);

    if (isValidChecked) {
      const newSelection = state.selectedFiles.filter((f) => f.url !== file.url);
      dispatch({ type: "SET_SELECTED_FILES", payload: newSelection });
      return;
    }
    if (isInvalidChecked) {
      removeInvalidSelectionByUrl(file.url);
      return;
    }

    const candidate = {
      url: file.url,
      name: file.name,
      size_bytes: file.size_bytes,
      drive_id: file.driveId,
      item_id: file.id,
    };
    const validation = splitSelectionByLikelyBackendPolicy([candidate]);
    if (validation.rejected.length > 0) {
      upsertInvalidSelection(validation.rejected[0]);
      return;
    }

    removeInvalidSelectionByUrl(file.url);
    dispatch({
      type: "SET_SELECTED_FILES",
      payload: [...state.selectedFiles, candidate],
    });
  };

  // Ensure the whole batch of fetched files is selected in one action for quicker setup.
  const handleSelectAll = () => {
    if (fetchedFiles.length === 0) {
      return; // Early exit keeps dispatch noise down when nothing is available yet.
    }

    const candidates = fetchedFiles.flatMap((file) =>
      file.driveId && file.id
        ? [
            {
              url: file.url,
              name: file.name,
              size_bytes: file.size_bytes,
              drive_id: file.driveId,
              item_id: file.id,
            },
          ]
        : []
    );
    const validation = splitSelectionByLikelyBackendPolicy(candidates);
    setInvalidSelections(validation.rejected);
    dispatch({
      type: "SET_SELECTED_FILES",
      payload: validation.accepted,
    });
  };

  // Allow users to clear a previously selected set of files without reloading the folder.
  const handleClearAll = () => {
    if (state.selectedFiles.length === 0 && invalidSelections.length === 0) {
      return; // Keeps unnecessary context updates to a minimum when nothing is selected.
    }

    dispatch({ type: "SET_SELECTED_FILES", payload: [] });
    setInvalidSelections([]);
  };

  const proceedWithSelection = () => {
    const last = path[path.length - 1];
    const sourceUrl = last?.webUrl || "";
    if (!last?.driveId || !last.itemId) {
      setError("The selected SharePoint folder is missing its identifiers. Choose it again.");
      return;
    }
    dispatch({
      type: "SET_SOURCE_FOLDER",
      payload: { url: sourceUrl, driveId: last.driveId, itemId: last.itemId },
    });
    onNavigate("memberSelection");
  };

  const handleContinue = () => {
    if (invalidSelections.length > 0) {
      setPendingRejectedFiles(invalidSelections);
      setIsFileValidationDialogOpen(true);
      return;
    }

    proceedWithSelection();
  };

  const handleContinueWithAcceptedFiles = () => {
    setIsFileValidationDialogOpen(false);

    if (state.selectedFiles.length === 0) {
      setError(
        "All selected files will be skipped. Add at least one supported file before continuing."
      );
      return;
    }

    proceedWithSelection();
  };

  /** Re-expand the folder browser so the user can pick a different folder. */
  const handleChangeFolderClick = () => {
    setIsFolderBrowserExpanded(true);
  };

  const listSelectedFolderFiles = async (driveId: string, itemId: string, folderLabel?: string) => {
    const isCurrentSource = sourceRequests.capture();
    setIsFilesLoading(true);
    setError(null);
    setFetchedFiles([]);
    dispatch({ type: "SET_SELECTED_FILES", payload: [] });
    setInvalidSelections([]);
    try {
      const token = await getAccessToken(instance);
      const data = await listFilesByIds(driveId, itemId, token);
      if (!isCurrentSource()) return;
      setFetchedFiles(data.files || []);

      if ((data.files || []).length === 0) {
        // Empty folder — keep the tree expanded so the user can pick another.
        setEmptyFolderName(folderLabel ?? "This folder");
        setIsEmptyFolderDialogOpen(true);
      } else {
        // Files loaded successfully — collapse the tree to maximise file list space.
        setIsFolderBrowserExpanded(false);
      }
    } catch (e: any) {
      if (isCurrentSource()) setError(e?.message || "Failed to list files");
    } finally {
      if (isCurrentSource()) setIsFilesLoading(false);
    }
  };

  const renderNode = (id: string, depth: number = 0) => {
    const n = nodes[id];
    if (!n) {
      return null;
    }
    const isSelected =
      selectedFolder && selectedFolder.itemId === n.itemId && selectedFolder.driveId === n.driveId;
    const onLoadMore = async (e: React.MouseEvent) => {
      e.stopPropagation();
      if (nodes[id]?.nextSkipToken) await loadChildren(id, true, n?.driveId, n?.itemId);
    };
    const childIds = nodes[id]?.children ?? [];
    return (
      <TreeItem key={id} itemType="branch" value={id}>
        <TreeItemLayout
          onClick={() => {
            if (n?.driveId && n?.itemId) {
              setSelectedFolder({
                driveId: n.driveId,
                itemId: n.itemId,
                name: n.name,
                webUrl: n.webUrl,
              });
            }
          }}
        >
          <span
            className={mergeClasses(styles.treeNode, isSelected && styles.treeNodeSelected)}
            style={{ marginLeft: depth > 0 ? depth * 8 : 0 }}
            title={n?.name}
          >
            <FolderRegular style={{ color: "#F7D154" }} /> {n?.name}
            {n?.isLoading ? <span style={{ marginLeft: 8 }}>Loading…</span> : null}
            {n?.isLoaded && n?.nextSkipToken ? (
              <Button
                appearance="secondary"
                className={styles.secondaryButton}
                size="small"
                onClick={onLoadMore}
              >
                Load more
              </Button>
            ) : null}
          </span>
        </TreeItemLayout>
        <Tree>
          {openItems.has(id) ? childIds.map((childId) => renderNode(childId, depth + 1)) : null}
        </Tree>
      </TreeItem>
    );
  };

  const continueLabel = "Continue to Member Selection";
  const rejectedPreviewCount = invalidSelections.length;
  const rejectedPreviewItems = invalidSelections;
  const selectedFolderLabel = path[path.length - 1]?.name ?? "";

  return (
    <main className={styles.root}>
      {/* ── Header ──────────────────────────────────────────── */}
      <Card className={styles.headerCard}>
        <Title3 className={styles.headerTitle}>Select source files</Title3>
        <Body1 className={styles.headerBody}>
          Step 1 · Choose files from your Sourcestone workspace for member data extraction.
        </Body1>
      </Card>

      {/* ── Scrollable main area ────────────────────────────── */}
      <div className={styles.main}>
        {/* Loading spinner while storage sources are being resolved. */}
        {isAppFolderLoading && (
          <div style={{ paddingTop: 8 }}>
            <Spinner label="Loading storage sources..." />
          </div>
        )}

        {/* App folder error — user needs to run setup first */}
        {appFolderError && (
          <MessageBar className={styles.warningMessage} intent="warning">
            <MessageBarBody>
              <strong>No storage source is available.</strong> {appFolderError}
            </MessageBarBody>
            <MessageBarActions>
              <Button disabled={isAppFolderLoading} onClick={() => void reloadSources()}>
                Retry
              </Button>
            </MessageBarActions>
          </MessageBar>
        )}

        {sourceWarning && !appFolderError && (
          <MessageBar intent="warning">
            {sourceWarning && <MessageBarBody>{sourceWarning}</MessageBarBody>}
            <MessageBarActions>
              <Button disabled={isAppFolderLoading} onClick={() => void reloadSources()}>
                Retry
              </Button>
            </MessageBarActions>
          </MessageBar>
        )}

        {/* General errors (file listing failures etc.) */}
        {!appFolderError && error && (
          <MessageBar className={styles.errorMessage} intent="error">
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        )}

        {!appFolderError && availableSources.length > 1 && selectedSource && (
          <Card className={styles.panelCard}>
            <Text weight="semibold">Storage source</Text>
            <Dropdown
              aria-label="Storage source"
              value={selectedSource.name}
              selectedOptions={[selectedSource.id]}
              onOptionSelect={(_, data) => setSelectedSourceId(String(data.optionValue))}
            >
              {availableSources.map((source) => (
                <Option key={source.id} value={source.id} text={source.name}>
                  {source.name}
                </Option>
              ))}
            </Dropdown>
          </Card>
        )}

        {/* ── Folder browser — EXPANDED state ─────────────── */}
        {/* Full tree + hint text + "Select folder" button.     */}
        {/* Visible on first load and when the user clicks      */}
        {/* "Change" on the collapsed summary row.              */}
        {!appFolderError && appFolder && isFolderBrowserExpanded && (
          <Card className={mergeClasses(styles.folderPanel, styles.panelCard)}>
            <div className={styles.panelHeadingRow}>
              <div className={styles.iconBox} aria-hidden>
                <FolderOpen24Regular />
              </div>
              <div>
                <p className={styles.panelHeading}>Choose a source folder</p>
                <Text className={styles.folderHint}>
                  Browse inside <strong>{appFolder.name}</strong>, then select a folder.
                </Text>
              </div>
            </div>
            <div className={styles.folderTreeFrame}>
              <div className={styles.folderTreeScroll}>
                {isFoldersLoading && !isAppFolderLoading && (
                  <div style={{ padding: "8px 0" }}>
                    <Spinner label="Loading folders..." />
                  </div>
                )}
                <Tree
                  aria-label="Sourcestone folder tree"
                  openItems={[...openItems]}
                  onOpenChange={async (_, data) => {
                    const toggledId = String(data.value);
                    const isOpen = Boolean(data.open);
                    const node = nodesRef.current[toggledId];
                    const shouldLoadChildren = isOpen && !node?.isLoaded && !node?.isLoading;
                    const driveIdToLoad = shouldLoadChildren ? node?.driveId : undefined;
                    const itemIdToLoad = shouldLoadChildren ? node?.itemId : undefined;

                    setOpenItems(updateOpenFolderItems(data.openItems, toggledId, isOpen, nodes));

                    if (driveIdToLoad && itemIdToLoad) {
                      await loadChildren(toggledId, false, driveIdToLoad, itemIdToLoad);
                    }
                  }}
                >
                  {renderNode("root")}
                </Tree>
              </div>
            </div>

            {/* Action buttons: "Select folder" + optional "Cancel" when re-expanding */}
            <div className={styles.folderActions}>
              <Button
                appearance="secondary"
                className={styles.primaryButton}
                disabled={
                  !selectedFolder || isFoldersLoading || isFilesLoading || Boolean(appFolderError)
                }
                icon={<FolderOpen24Regular />}
                onClick={() => {
                  if (!selectedFolder) return;
                  setPath([
                    {
                      driveId: selectedFolder.driveId,
                      itemId: selectedFolder.itemId,
                      name: selectedFolder.name,
                      webUrl: selectedFolder.webUrl,
                    },
                  ]);
                  void listSelectedFolderFiles(
                    selectedFolder.driveId,
                    selectedFolder.itemId,
                    selectedFolder.name
                  );
                }}
              >
                {isFoldersLoading || isFilesLoading ? "Loading..." : "Select folder"}
              </Button>

              {/* Cancel button — only shown when re-expanding after initial folder pick */}
              {hasFolderSelection && (
                <Button
                  appearance="secondary"
                  className={styles.secondaryButton}
                  size="small"
                  onClick={() => setIsFolderBrowserExpanded(false)}
                >
                  Cancel
                </Button>
              )}
            </div>
          </Card>
        )}

        {/* ── Folder browser — COLLAPSED state ────────────── */}
        {/* Compact summary: 📁 FolderName ·········· [Change] */}
        {/* Frees ~200px of vertical space for the file list.  */}
        {!appFolderError && appFolder && !isFolderBrowserExpanded && hasFolderSelection && (
          <Card className={styles.folderSummaryRow}>
            <span className={styles.iconBox} aria-hidden>
              <FolderOpen24Regular />
            </span>
            <Text className={styles.folderSummaryName} title={selectedFolderLabel}>
              {selectedFolderLabel}
            </Text>
            <Button
              appearance="secondary"
              className={styles.secondaryButton}
              size="small"
              onClick={handleChangeFolderClick}
            >
              Change
            </Button>
          </Card>
        )}

        {/* ── Files panel ─────────────────────────────────── */}
        {hasFolderSelection && (
          <Card className={mergeClasses(styles.filesPanel, styles.panelCard)}>
            {/* Compact header: title + count on the left, action buttons on the right */}
            <div className={styles.filesHeaderRow}>
              <div className={styles.filesHeaderLeft}>
                <span className={styles.smallIconBox} aria-hidden>
                  <DocumentMultiple24Regular />
                </span>
                <Subtitle2 className={styles.filesTitle}>Files</Subtitle2>
                <Text className={styles.filesCount}>
                  {state.selectedFiles.length} of {fetchedFiles.length} selected
                </Text>
              </div>
              <div className={styles.filesHeaderButtons}>
                <Button
                  appearance="secondary"
                  className={styles.secondaryButton}
                  icon={<Checkmark24Regular />}
                  size="small"
                  onClick={handleSelectAll}
                  disabled={fetchedFiles.length === 0}
                >
                  Select all
                </Button>
                <Button
                  appearance="secondary"
                  className={styles.secondaryButton}
                  icon={<Dismiss24Regular />}
                  size="small"
                  onClick={handleClearAll}
                  disabled={state.selectedFiles.length === 0 && invalidSelections.length === 0}
                >
                  Clear
                </Button>
              </div>
            </div>

            {/* Loading indicator while files are being fetched */}
            {isFilesLoading && (
              <div style={{ paddingTop: 4 }}>
                <Spinner label="Loading files..." />
              </div>
            )}

            {/* Empty state */}
            {!isFilesLoading && fetchedFiles.length === 0 && (
              <Body1>No files found in this folder.</Body1>
            )}

            {/* File list with internal scroll */}
            {!isFilesLoading && fetchedFiles.length > 0 && (
              <div className={styles.filesListFrame}>
                <div className={styles.filesListScroll}>
                  {fetchedFiles.map((file, index) => (
                    <div key={`file-${index}`} className={styles.fileRow}>
                      <Checkbox
                        checked={
                          state.selectedFiles.some((f) => f.url === file.url) ||
                          invalidSelections.some((item) => item.file.url === file.url)
                        }
                        onChange={() => handleFileSelection(file)}
                      />
                      <Document24Regular className={styles.fileIcon} aria-hidden />
                      <Body1>{file.name}</Body1>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Inline warning for files that will be skipped */}
            {rejectedPreviewCount > 0 && (
              <div className={styles.rejectedPreviewList}>
                <Text className={styles.rejectedPreviewTitle}>
                  {rejectedPreviewCount} file{rejectedPreviewCount === 1 ? "" : "s"} will be skipped
                </Text>
                {rejectedPreviewItems.map((entry) => (
                  <Text
                    key={`preview-${entry.file.url}-${entry.file.name}`}
                    className={styles.rejectedPreviewItem}
                  >
                    {entry.file.name}: {entry.reason}
                  </Text>
                ))}
              </div>
            )}
          </Card>
        )}
      </div>

      {/* ── Footer ──────────────────────────────────────────── */}
      <Card className={styles.footerCard}>
        <div className={styles.footerActions}>
          <Button
            appearance="primary"
            className={mergeClasses(styles.footerButton, "sourcestone-navigation-primary")}
            icon={<ArrowLeft24Regular />}
            onClick={() => onNavigate("taskSelection")}
          >
            <span className={styles.footerLabelLong}>Back to Task Selection</span>
            <span className={styles.footerLabelShort}>Back</span>
          </Button>
          <Button
            appearance="primary"
            className={mergeClasses(styles.footerButton, "sourcestone-navigation-primary")}
            icon={<ArrowRight24Regular />}
            iconPosition="after"
            onClick={handleContinue}
            disabled={state.selectedFiles.length === 0 && invalidSelections.length === 0}
          >
            <span className={styles.footerLabelLong}>{continueLabel}</span>
            <span className={styles.footerLabelShort}>Continue</span>
          </Button>
        </div>
      </Card>

      {/* ── Dialogs ─────────────────────────────────────────── */}
      <Dialog
        open={isEmptyFolderDialogOpen}
        onOpenChange={(_, data) => {
          if (!data.open) setIsEmptyFolderDialogOpen(false);
        }}
      >
        <DialogSurface className={styles.dialogSurface}>
          <DialogBody>
            <DialogTitle>Folder is empty</DialogTitle>
            <DialogContent>
              {emptyFolderName ?? "The selected folder"} has no files. Please choose another folder.
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                className={styles.primaryButton}
                onClick={() => setIsEmptyFolderDialogOpen(false)}
              >
                OK
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <Dialog
        open={isFileValidationDialogOpen}
        onOpenChange={(_, data) => {
          if (!data.open) {
            setIsFileValidationDialogOpen(false);
          }
        }}
      >
        <DialogSurface className={styles.dialogSurface}>
          <DialogBody>
            <DialogTitle>Some files will be skipped</DialogTitle>
            <DialogContent>
              {pendingRejectedFiles.length} selected file
              {pendingRejectedFiles.length === 1 ? "" : "s"} will be skipped:
              <ul
                style={{
                  marginTop: "8px",
                  marginBottom: 0,
                  paddingLeft: "18px",
                  maxHeight: "140px",
                  overflowY: "auto",
                }}
              >
                {pendingRejectedFiles.map((entry) => (
                  <li key={`${entry.file.url}-${entry.file.name}`}>
                    {entry.file.name}: {entry.reason}
                  </li>
                ))}
              </ul>
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                className={styles.secondaryButton}
                onClick={() => setIsFileValidationDialogOpen(false)}
              >
                Review selection
              </Button>
              <Button
                appearance="primary"
                className="sourcestone-navigation-primary"
                onClick={handleContinueWithAcceptedFiles}
              >
                Continue with supported files
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </main>
  );
};
